import { summarizeDiffSize, summarizeReviewWeight, type CodeMapCompilation, type ReviewWeightBand } from "../../shared/codemap";
import type { ChewConfig } from "../../shared/config";
import type { PrimaryNature } from "../../shared/guide";
import {
  BarChart3,
  Braces,
  Cable,
  CircleEllipsis,
  Cog,
  Database,
  Eye,
  FlaskConical,
  GitBranch,
  Monitor,
  Plug,
  Route,
  ScanLine,
  Search,
  Server,
  Shield,
  type LucideIcon,
} from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { natureLabels } from "./CoverageReceipt";

const natureColors: Record<PrimaryNature, string> = {
  authorization: "#b85b55",
  "business-logic": "#32865a",
  "controller-entrypoint": "#6f899c",
  contract: "#4f8d8a",
  "data-persistence": "#597fab",
  integration: "#b47a35",
  "tests-proof": "#7d8994",
  generated: "#b7bbb8",
  "wiring-config": "#82927e",
  frontend: "#4b91a8",
  infrastructure: "#a96c45",
  "analytics-data-model": "#8174a2",
  other: "#9a9a9f",
};

const scrutinyLabels = { deep: "Deep", standard: "Standard", scan: "Scan" } as const;
const weightBandLabels: Record<ReviewWeightBand, string> = {
  light: "light review",
  moderate: "moderate review",
  heavy: "heavy review",
  "very-heavy": "very heavy review",
};
const scrutinyColors = { deep: "#b85b55", standard: "#c08a3e", scan: "#9fa7a2" } as const;

const naturePresentation: Record<PrimaryNature, { icon: LucideIcon; shortLabel: string; foreground?: string }> = {
  authorization: { icon: Shield, shortLabel: "Auth" },
  "business-logic": { icon: GitBranch, shortLabel: "Logic" },
  "controller-entrypoint": { icon: Route, shortLabel: "Entry" },
  contract: { icon: Braces, shortLabel: "Contracts" },
  "data-persistence": { icon: Database, shortLabel: "Data" },
  integration: { icon: Plug, shortLabel: "Integration" },
  "tests-proof": { icon: FlaskConical, shortLabel: "Tests" },
  generated: { icon: Cog, shortLabel: "Generated", foreground: "#2f3833" },
  "wiring-config": { icon: Cable, shortLabel: "Wiring" },
  frontend: { icon: Monitor, shortLabel: "Frontend" },
  infrastructure: { icon: Server, shortLabel: "Infra" },
  "analytics-data-model": { icon: BarChart3, shortLabel: "Models" },
  other: { icon: CircleEllipsis, shortLabel: "Other" },
};

const scrutinyPresentation = {
  deep: { icon: Search, shortLabel: "Deep" },
  standard: { icon: Eye, shortLabel: "Standard" },
  scan: { icon: ScanLine, shortLabel: "Scan", foreground: "#2f3833" },
} satisfies Record<"scan" | "standard" | "deep", { icon: LucideIcon; shortLabel: string; foreground?: string }>;

interface Segment {
  color: string;
  foreground?: string;
  icon: LucideIcon;
  label: string;
  loc: number;
  shortLabel: string;
}

function percentage(value: number, total: number) {
  return total === 0 ? 0 : Math.round((value / total) * 100);
}

function SegmentedBreakdown({ description, segments, title }: { description: string; segments: Segment[]; title: string }) {
  const total = segments.reduce((sum, segment) => sum + segment.loc, 0);
  const label = segments.map((segment) => `${segment.label} ${percentage(segment.loc, total)}%`).join(", ");
  return (
    <section className="change-breakdown">
      <header>
        <div><strong>{title}</strong><span>{description}</span></div>
        <small>{total.toLocaleString()} LOC</small>
      </header>
      <TooltipProvider delay={120}>
        <div aria-label={`${title}: ${label}`} className="change-segmented-bar" role="group">
          {segments.map((segment) => {
            const share = percentage(segment.loc, total);
            const Icon = segment.icon;
            return (
              <Tooltip key={segment.label}>
                <TooltipTrigger
                  render={
                    <button
                      aria-label={`${segment.label}: ${share}%, ${segment.loc.toLocaleString()} changed LOC`}
                      className={`change-bar-segment${share >= 8 ? " segment-labelled" : share >= 2 ? " segment-icon" : " segment-sliver"}`}
                      style={{ backgroundColor: segment.color, color: segment.foreground ?? "#fff", width: `${(segment.loc / total) * 100}%` }}
                      type="button"
                    />
                  }
                >
                  {share >= 2 && <Icon aria-hidden="true" />}
                  {share >= 8 && <strong>{segment.shortLabel}</strong>}
                  {share >= 8 && <small>{share}%</small>}
                </TooltipTrigger>
                <TooltipContent>
                  <strong>{segment.label}</strong>
                  <span>{share}%</span>
                  <span>·</span>
                  <span>{segment.loc.toLocaleString()} LOC</span>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </section>
  );
}

export function ChangeProfile({ compilation, config }: { compilation: CodeMapCompilation; config: ChewConfig }) {
  const diff = summarizeDiffSize(compilation, config);
  const weight = summarizeReviewWeight(compilation, config);
  const byNature = new Map<PrimaryNature, number>();
  const byScrutiny = new Map<"scan" | "standard" | "deep", number>();
  for (const region of compilation.regions) {
    const loc = region.changedLoc ?? 0;
    byNature.set(region.nature, (byNature.get(region.nature) ?? 0) + loc);
    byScrutiny.set(region.scrutiny, (byScrutiny.get(region.scrutiny) ?? 0) + loc);
  }
  const natureSegments = [...byNature.entries()]
    .map(([nature, loc]) => ({ color: natureColors[nature], label: natureLabels[nature], loc, ...naturePresentation[nature] }))
    .filter((segment) => segment.loc > 0)
    .sort((left, right) => right.loc - left.loc);
  const scrutinySegments = (["deep", "standard", "scan"] as const)
    .map((scrutiny) => ({ color: scrutinyColors[scrutiny], label: scrutinyLabels[scrutiny], loc: byScrutiny.get(scrutiny) ?? 0, ...scrutinyPresentation[scrutiny] }))
    .filter((segment) => segment.loc > 0);

  return (
    <section aria-labelledby="change-profile-title" className="change-profile">
      <header>
        <span className="quiet-label">Change profile</span>
        <h2 id="change-profile-title">What changed, and where attention goes</h2>
      </header>
      <div className="diff-size-summary">
        <article
          className="diff-size-primary"
          title={`Excludes ${diff.excludedNatures.map((nature) => natureLabels[nature]).join(", ")} according to .chew/config.yaml`}
        >
          <span>Effective diff</span>
          <strong>{diff.effectiveChangedLoc.toLocaleString()} LOC</strong>
          <small>the code you actually have to judge</small>
        </article>
        <article
          className={`diff-size-weight diff-size-weight-${weight.band}`}
          title="Changed LOC weighted by the scrutiny and concerns each region declared, expressed as standard-scrutiny equivalent."
        >
          <span>Review load</span>
          <strong>{weight.weightedLoc.toLocaleString()} LOC</strong>
          <small>standard-review-equivalent · {weightBandLabels[weight.band]}{weight.densityRatio >= 1.1 ? ` · ${weight.densityRatio}× its size` : ""}</small>
        </article>
        <article className="diff-size-secondary">
          <span>Total diff</span>
          <strong>{diff.totalChangedLoc.toLocaleString()} LOC</strong>
          <small>{diff.excludedChangedLoc.toLocaleString()} excluded as noise</small>
        </article>
      </div>
      <SegmentedBreakdown description="Primary purpose of each changed line" segments={natureSegments} title="PR contents" />
      <SegmentedBreakdown description="Required scrutiny, independent of code type" segments={scrutinySegments} title="Review attention" />
    </section>
  );
}
